import React from 'react';
import { Upload, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { default as api } from '@/lib/api';

/**
 * PolicyPdfField — one-shot PDF uploader tied to a specific policy kind
 * (privacy / terms / ethics / pprcic). Uploads to
 * POST /api/admin/settings/policy-upload/{kind} and updates the parent
 * URL field on success. Shows the currently linked PDF (if any).
 */
export const PolicyPdfField = ({ kind, value, onChange, onUploaded, testidPrefix }) => {
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [showPreview, setShowPreview] = React.useState(false);

  const isBackendPdf = value && value.startsWith('/api/public/files/');
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const fullUrl = isBackendPdf ? `${backendUrl}${value}` : value;
  // A field can be previewed inline if we know it's a PDF (either uploaded to
  // our backend, or an external URL that ends with `.pdf`). External web pages
  // are opened in a new tab instead.
  const isPdf = isBackendPdf || (value && /\.pdf(\?|$)/i.test(value));

  const handlePick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setError('Apenas ficheiros PDF são aceites'); return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Ficheiro excede 10 MB'); return;
    }
    setError(''); setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/admin/settings/policy-upload/${kind}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
      setShowPreview(true);
      onUploaded?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar ficheiro');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          onChange={handleFile}
          className="hidden"
          data-testid={`${testidPrefix}-file-input`}
        />
        <Button
          type="button"
          variant="outline"
          onClick={handlePick}
          disabled={uploading}
          className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
          data-testid={`${testidPrefix}-upload-btn`}
        >
          {uploading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> A carregar…</>
          ) : (
            <><Upload className="w-4 h-4 mr-2" /> {isBackendPdf ? 'Substituir PDF' : 'Carregar PDF'}</>
          )}
        </Button>
        {isPdf && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowPreview(v => !v)}
            className="text-indigo-700 hover:bg-indigo-50"
            data-testid={`${testidPrefix}-toggle-preview-btn`}
          >
            {showPreview ? (<><EyeOff className="w-4 h-4 mr-1" /> Ocultar pré-visualização</>)
                         : (<><Eye className="w-4 h-4 mr-1" /> Pré-visualizar</>)}
          </Button>
        )}
        {value && (
          <a
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-indigo-700 hover:underline ml-1"
            data-testid={`${testidPrefix}-open-newtab`}
          >
            Abrir em nova aba ↗
          </a>
        )}
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {!value && (
        <p className="text-xs text-slate-500">Ou introduza um link/URL de PDF externo abaixo.</p>
      )}

      {isPdf && showPreview && (
        <div className="mt-2 border border-slate-300 rounded-md overflow-hidden bg-slate-100" data-testid={`${testidPrefix}-preview-container`}>
          <iframe
            src={fullUrl}
            title={`PDF preview ${kind}`}
            className="w-full block"
            style={{ height: 520 }}
            data-testid={`${testidPrefix}-preview-iframe`}
          />
          <div className="text-[11px] text-slate-500 px-3 py-1.5 border-t border-slate-200 bg-slate-50">
            Pré-visualização renderizada pelo browser. Alguns URLs externos podem bloquear a incorporação.
          </div>
        </div>
      )}
    </div>
  );
};
